/**
 * Registro dos painéis (categorias de camada): Satélite, Queimadas, Mapa e, no
 * futuro, Previsão, Estações, Sondagens. O trilho (desktop) e a grade de
 * camadas da folha (celular) leem daqui; adicionar uma categoria é registrar
 * um painel, sem mexer em `App.tsx`, `Rail` ou `MobileSheet`.
 *
 * O registro acontece na importação de `ui/modules.tsx` (ponto único de
 * integração), antes do primeiro render.
 */
import type { LucideIcon } from 'lucide-react';
import type { ComponentType } from 'react';

/** Cor de acento de um painel: verde (satélite, mapa), laranja (fogo), azul (água). */
export type PanelTone = 'mata' | 'fogo' | 'rio';

/**
 * Interruptor da camada de um painel, usado pelo trilho e pelo cabeçalho do
 * painel (desktop) e pelos blocos e ajustes da folha (celular). Liga/desliga a camada INTEIRA com um
 * clique; as opções (sub-produtos, aparência, legenda) continuam no painel.
 *
 * Camada com vários sub-produtos (pilha do satélite, fontes dos focos,
 * camadas do mapa) desliga tudo e, ao religar, restaura a mesma seleção —
 * ou o padrão, se nada estava selecionado.
 */
export interface LayerSwitch {
  /** Hook: a camada aparece no mapa? */
  useOn: () => boolean;
  /** O mesmo, fora do React (o "Próximos dias" desliga e religa as camadas observadas). */
  isOn: () => boolean;
  setOn: (on: boolean) => void;
}

export interface PanelDef {
  /** Identificador estável (vai para a URL e para o estado). */
  id: string;
  label: string;
  icon: LucideIcon;
  Panel: ComponentType;
  /** Ordem no trilho e nas abas (menor primeiro). */
  order: number;
  /** Hook opcional: true quando o painel tem um aviso a mostrar (ponto amarelo). */
  useWarning?: () => boolean;
  /** Cor do ícone e dos chips com a camada ligada (padrão: verde). */
  tone?: PanelTone;
  /** Interruptor da camada; um painel que só configura (sem camada própria) omite. */
  layer?: LayerSwitch;
  /**
   * Hook opcional: o QUE está ligado, em poucas palavras, para camadas com
   * vários produtos ("GeoColor + 1", "2 fontes", "3 camadas"). Lido pelos
   * blocos da folha do celular; `null` com a camada desligada.
   */
  useSummary?: () => string | null;
  /**
   * Data (AAAA-MM-DD) até a qual o painel exibe o selo de novidade, para quem
   * ainda não o abriu. Ver `ui/novidade.ts`.
   */
  novoAte?: string;
}

const panels: PanelDef[] = [];

export function registerPanel(def: PanelDef): void {
  const i = panels.findIndex((p) => p.id === def.id);
  if (i >= 0) panels[i] = def;
  else panels.push(def);
  panels.sort((a, b) => a.order - b.order);
}

/** Painéis registrados, em ordem. Lista estável após a inicialização. */
export function getPanels(): readonly PanelDef[] {
  return panels;
}

export function getPanel(id: string): PanelDef | undefined {
  return panels.find((p) => p.id === id);
}
